import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <footer 
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="bg-[#07192C] text-[#94A3B8] pt-16 pb-12 px-6 sm:px-12 border-t border-slate-800/80 relative font-sans overflow-hidden transition-all duration-300"
    >
      {/* Dynamic Blue Ambient Spotlight */}
      <div 
        className="pointer-events-none absolute inset-0 transition-opacity duration-300"
        style={{
          opacity: isHovered ? 1 : 0,
          background: `radial-gradient(750px circle at ${mousePos.x}px ${mousePos.y}px, rgba(37, 99, 235, 0.22), rgba(22, 50, 91, 0.08) 50%, transparent 80%)`,
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Main 5-Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-8 lg:gap-10 pb-14 border-b border-slate-800/80 items-start">
          
          {/* Column 1: Rounded White Card Logo + Company Details */}
          <div className="md:col-span-3 space-y-4">
            
            <Link 
              to="/" 
              className="inline-flex bg-white rounded-2xl w-44 h-[78px] overflow-hidden items-center justify-center shadow-lg border border-white/30 transition-transform duration-300 hover:scale-[1.02]"
            >
              <img 
                src="/logo-black1.png" 
                alt="Welvate" 
                className="w-20 h-full object-contain scale-[1.95] transform"
              />
            </Link>

            <div className="space-y-1.5 text-xs sm:text-[13px] pt-1">
              <p className="text-slate-100 font-semibold leading-relaxed">
                Welvate — wealth management &amp; financial education.
              </p>
              <p className="text-slate-400 leading-relaxed text-xs">
                Paschim Vihar, New Delhi, India 110063
              </p>
            </div>

            <div className="space-y-2.5 pt-2 text-xs sm:text-[13px] text-slate-300 font-medium border-t border-slate-800/60">
              <a href="mailto:support@welvate.com" className="flex items-center gap-2.5 hover:text-white transition">
                <span className="text-sm">✉️</span>
                <span>support@welvate.com</span>
              </a>
              <a href="tel:+919876543210" className="flex items-center gap-2.5 hover:text-white transition">
                <span className="text-[#60A5FA] text-sm">📞</span>
                <span className="text-slate-200">+91 98765 43210</span>
              </a>
            </div>

          </div>

          {/* Column 2: Company */}
          <div className="md:col-span-2 space-y-4">
            <div>
              <h4 className="font-serif-title text-base sm:text-lg font-bold text-white tracking-wide">
                Company
              </h4>
              <div className="w-8 h-0.5 bg-[#3B82F6] mt-1 rounded-full" />
            </div>
            <ul className="space-y-3 text-xs sm:text-[13px] font-normal text-slate-300">
              <li><Link to="/about" className="hover:text-white transition block">About Haritika</Link></li>
              <li><Link to="/about" className="hover:text-white transition block">Mission &amp; Vision</Link></li>
              <li><Link to="/courses" className="hover:text-white transition block">Student Courses</Link></li>
              <li><Link to="/faq" className="hover:text-white transition block">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-white transition block">Contact Us</Link></li>
            </ul>
          </div>

          {/* Column 3: Legal & Policy */}
          <div className="md:col-span-2 space-y-4">
            <div>
              <h4 className="font-serif-title text-base sm:text-lg font-bold text-white tracking-wide">
                Legal &amp; Policy
              </h4>
              <div className="w-8 h-0.5 bg-[#3B82F6] mt-1 rounded-full" />
            </div>
            <ul className="space-y-3 text-xs sm:text-[13px] font-normal text-slate-300">
              <li><Link to="/faq" state={{ tab: 'privacy' }} className="hover:text-white transition block">Privacy Policy</Link></li>
              <li><Link to="/faq" state={{ tab: 'disclaimer' }} className="hover:text-white transition block">Terms &amp; Conditions</Link></li>
              <li><Link to="/faq" state={{ tab: 'disclaimer' }} className="hover:text-white transition block">Risk Disclosure</Link></li>
              <li><Link to="/faq" state={{ tab: 'disclaimer' }} className="hover:text-white transition block">Grievance Policy</Link></li>
              <li><Link to="/faq" state={{ tab: 'disclaimer' }} className="hover:text-white transition block">Compliance</Link></li>
            </ul>
          </div>

          {/* Column 4: Products */}
          <div className="md:col-span-2 space-y-4">
            <div>
              <h4 className="font-serif-title text-base sm:text-lg font-bold text-white tracking-wide">
                Products
              </h4>
              <div className="w-8 h-0.5 bg-[#3B82F6] mt-1 rounded-full" />
            </div>
            <ul className="space-y-3 text-xs sm:text-[13px] font-normal text-slate-300">
              <li><Link to="/products/mutual-funds" className="hover:text-white transition block">Mutual Funds</Link></li>
              <li><Link to="/products/pms" className="hover:text-white transition block">PMS &amp; AIF</Link></li>
              <li><Link to="/products/bonds-fds" className="hover:text-white transition block">Bonds / NCDs &amp; FDs</Link></li>
              <li><Link to="/products/pre-ipo" className="hover:text-white transition block">Pre-IPO Unlisted</Link></li>
              <li><Link to="/products/offshore" className="hover:text-white transition block">International Platforms</Link></li>
              <li><Link to="/courses" className="hover:text-white transition block">Learning Paths</Link></li>
            </ul>
          </div>

          {/* Column 5: Right White Card + Social Icons */}
          <div className="md:col-span-3 space-y-5">
            {/* White Call-to-Action Card */}
            <div className="bg-white rounded-2xl py-7 px-5 text-center shadow-xl">
              <h4 className="font-serif-title text-lg font-bold text-[#07192C] leading-snug mb-4">
                Start your wealth<br />journey
              </h4>
              <Link 
                to="/apply" 
                className="inline-block bg-[#16325B] hover:bg-[#0F213D] text-white text-xs sm:text-sm font-bold px-7 py-3 rounded-xl transition shadow-md hover:shadow-lg"
              >
                Join Waitlist
              </Link>
            </div>

            {/* Social Icons Section */}
            <div className="space-y-2.5 pt-1">
              <h5 className="font-serif-title text-xs sm:text-sm font-bold text-white">Let's Connect</h5>
              <div className="flex items-center gap-2.5">
                <Link to="/contact" className="w-8 h-8 rounded-full bg-slate-800/90 border border-slate-700/80 hover:border-[#3B82F6] hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition text-xs font-bold">f</Link>
                <Link to="/courses" className="w-8 h-8 rounded-full bg-slate-800/90 border border-slate-700/80 hover:border-[#3B82F6] hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition text-[10px]">▶</Link>
                
                <a 
                  href="https://www.instagram.com/welvate_services?rpxt=ZWoyMGk3ZmR2ODZ4" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-slate-800/90 border border-slate-700/80 hover:border-[#3B82F6] hover:bg-[#16325B]/40 hover:text-[#60A5FA] flex items-center justify-center text-slate-300 transition"
                >
                  <svg className="w-4 h-4 fill-none stroke-current stroke-[2] stroke-linecap-round stroke-linejoin-round" viewBox="0 0 24 24">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>

                <Link to="/about" className="w-8 h-8 rounded-full bg-slate-800/90 border border-slate-700/80 hover:border-[#3B82F6] hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition text-xs font-semibold">in</Link>
                <Link to="/" className="w-8 h-8 rounded-full bg-slate-800/90 border border-slate-700/80 hover:border-[#3B82F6] hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition text-xs">🌐</Link>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Disclaimer Section */}
        <div className="pt-8 text-center space-y-3 max-w-5xl mx-auto text-xs sm:text-[13px] text-slate-400 leading-relaxed">
          <p className="font-medium text-slate-300">
            Serving investors and learners across all major cities in India — fully online.
          </p>
          
          <p className="font-bold text-slate-200 text-sm">
            © 2026 All Rights Reserved by Welvate
          </p>

          <p className="text-xs text-slate-500 leading-normal max-w-3xl mx-auto pt-1">
            Welvate provides financial education and distribution/advisory services under applicable certifications (NISM Equity Derivatives, NISM Mutual Fund Distribution, IRDAI Insurance). We do not provide stock tips or buy/sell calls. Investments in securities markets are subject to market risks; read all related documents carefully. Past performance is not a promise of future returns.
          </p>
        </div>

      </div>

      {/* Floating Call Button */}
      <a 
        href="tel:+919876543210" 
        title="Call Us"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#16325B] hover:bg-[#0F213D] border border-blue-400/30 text-white rounded-full flex items-center justify-center shadow-2xl z-50 transition-all duration-300 hover:scale-110"
      >
        <svg 
          className="w-6 h-6 fill-none stroke-white stroke-[2.2] stroke-linecap-round stroke-linejoin-round" 
          viewBox="0 0 24 24"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </a>
    </footer>
  );
}